import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, Select, Button, Typography, Spin, Row, Col, Empty, Tag } from 'antd';
import { TrophyOutlined, GiftOutlined, CrownOutlined, LoginOutlined, EnvironmentOutlined, CheckCircleOutlined, CloseCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { driverService } from '../../common/services/api';
import './DriverPage.css';

const { Title, Paragraph, Text } = Typography;

/**
 * Determine si un mois (format YYYY-MM) est le 3e mois d'un trimestre
 * T1: Fev-Avr (3e = 04), T2: Mai-Jul (3e = 07), T3: Aou-Oct (3e = 10), T4: Nov-Jan (3e = 01)
 * Retourne le label du trimestre si c'est un 3e mois, sinon null
 */
function getQuarterForThirdMonth(period) {
    if (!period) return null;
    const [yearStr, monthStr] = period.split('-');
    const year = parseInt(yearStr);
    const month = parseInt(monthStr);

    if (month === 4) return `${year}-T1`;
    if (month === 7) return `${year}-T2`;
    if (month === 10) return `${year}-T3`;
    if (month === 1) return `${year - 1}-T4`;

    return null;
}

const MONTH_NAMES = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const QUARTER_LABELS = {
    1: 'Trimestre 1 (Fevrier - Avril)',
    2: 'Trimestre 2 (Mai - Juillet)',
    3: 'Trimestre 3 (Aout - Octobre)',
    4: 'Trimestre 4 (Novembre - Janvier)'
};

const DriverRankingPage = () => {
    const [loading, setLoading] = useState(true);
    const [rankings, setRankings] = useState([]);
    const [myRanking, setMyRanking] = useState(null);
    const [period, setPeriod] = useState(null);
    const [availablePeriods, setAvailablePeriods] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [driverUser, setDriverUser] = useState(null);
    const [driverCity, setDriverCity] = useState(null);

    // Classement trimestriel
    const [isQuarterly, setIsQuarterly] = useState(false);
    const [quarterlyData, setQuarterlyData] = useState(null);
    const [quarterPeriod, setQuarterPeriod] = useState(null);
    const [myQuarterlyRanking, setMyQuarterlyRanking] = useState(null);

    const loadCities = useCallback(async () => {
        try {
            const response = await driverService.getCities();
            if (response.success) {
                setCities(response.data.cities || []);
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('driverToken');
        const userData = localStorage.getItem('driverUser');
        setIsLoggedIn(!!token);

        let cityId = null;
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setDriverUser(parsedUser);
                if (parsedUser.city) {
                    setDriverCity(parsedUser.city);
                    cityId = parsedUser.city._id;
                }
            } catch (e) {
                console.error('Erreur parsing user data:', e);
            }
        }

        if (!token) {
            loadCities();
        }

        loadRanking(null, cityId);
    }, [loadCities]);

    const loadRanking = async (selectedPeriod = null, cityId = null) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('driverToken');

            // Charger le classement mensuel pour obtenir les periodes disponibles
            const rankingResponse = await driverService.getRanking(selectedPeriod, cityId);
            if (rankingResponse.success) {
                const currentPeriod = selectedPeriod || rankingResponse.data.period;
                setPeriod(currentPeriod);
                setAvailablePeriods(rankingResponse.data.availablePeriods || []);

                // Verifier si c'est le 3e mois d'un trimestre
                const quarterLabel = getQuarterForThirdMonth(currentPeriod);

                if (quarterLabel) {
                    // 3e mois -> classement trimestriel
                    if (token) {
                        // Connecte: appeler my-quarterly-ranking (inclut top5 + mon analyse + stats)
                        const myQResponse = await driverService.getMyQuarterlyRanking(token, quarterLabel, cityId);
                        if (myQResponse.success && myQResponse.data) {
                            setIsQuarterly(true);
                            setQuarterlyData(myQResponse.data);
                            setQuarterPeriod(quarterLabel);
                            setMyQuarterlyRanking(myQResponse.data.myQuarterlyRanking);
                            setRankings([]);
                        } else {
                            setIsQuarterly(false);
                            setQuarterlyData(null);
                            setQuarterPeriod(null);
                            setMyQuarterlyRanking(null);
                            setRankings(rankingResponse.data.rankings || []);
                        }
                    } else {
                        // Non connecte: appeler quarterly-ranking public (sans stats sensibles)
                        const quarterResponse = await driverService.getQuarterlyRanking(quarterLabel, cityId);
                        if (quarterResponse.success && quarterResponse.data) {
                            setIsQuarterly(true);
                            setQuarterlyData(quarterResponse.data);
                            setQuarterPeriod(quarterLabel);
                            setMyQuarterlyRanking(null);
                            setRankings([]);
                        } else {
                            setIsQuarterly(false);
                            setQuarterlyData(null);
                            setQuarterPeriod(null);
                            setMyQuarterlyRanking(null);
                            setRankings(rankingResponse.data.rankings || []);
                        }
                    }
                } else {
                    // Mois normal -> classement mensuel
                    setIsQuarterly(false);
                    setQuarterlyData(null);
                    setQuarterPeriod(null);
                    setMyQuarterlyRanking(null);
                    setRankings(rankingResponse.data.rankings || []);
                }
            }

            // Charger ma position mensuelle si connecte
            if (token) {
                try {
                    const myRankingResponse = await driverService.getMyRanking(token, selectedPeriod, cityId);
                    if (myRankingResponse.success && myRankingResponse.data.myRanking) {
                        setMyRanking(myRankingResponse.data);
                    } else {
                        setMyRanking(null);
                    }
                } catch (err) {
                    console.error('Erreur chargement ma position:', err);
                    setMyRanking(null);
                }
            }
        } catch (error) {
            console.error('Erreur chargement classement:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePeriodChange = (value) => {
        const cityId = isLoggedIn ? (driverCity?._id || null) : selectedCity;
        loadRanking(value, cityId);
    };

    const handleCityChange = (value) => {
        setSelectedCity(value);
        loadRanking(period, value);
    };

    const formatPeriod = (p) => {
        if (!p) return '';
        const [year, month] = p.split('-');
        return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
    };

    const formatCurrency = (amount) => {
        const formatted = new Intl.NumberFormat('fr-FR').format(amount);
        let firstDigitFound = false;
        const masked = formatted.split('').map(char => {
            if (/\d/.test(char)) {
                if (!firstDigitFound) {
                    firstDigitFound = true;
                    return char;
                }
                return '*';
            }
            return char;
        }).join('');
        return masked + ' FCFA';
    };

    const formatFullCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
    };

    const formatPhone = (phone) => {
        if (!phone) return '';
        let cleaned = phone.replace('+225', '').replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
        }
        return cleaned;
    };

    const getRankBadgeClass = (rank) => {
        if (rank === 1) return 'gold';
        if (rank === 2) return 'silver';
        if (rank === 3) return 'bronze';
        return 'normal';
    };

    const handleLogout = () => {
        localStorage.removeItem('driverToken');
        localStorage.removeItem('driverUser');
        setIsLoggedIn(false);
        setMyRanking(null);
        setMyQuarterlyRanking(null);
        setDriverUser(null);
        setDriverCity(null);
        setSelectedCity(null);
        loadCities();
        loadRanking(period, null);
    };

    const getDisplayedCityName = () => {
        if (isLoggedIn && driverCity) {
            return driverCity.name;
        }
        if (selectedCity) {
            const city = cities.find(c => c._id === selectedCity);
            return city?.name || null;
        }
        return null;
    };

    const displayedCityName = getDisplayedCityName();

    const formatMonthShort = (monthStr) => {
        if (!monthStr) return '';
        const [, m] = monthStr.split('-');
        const shortNames = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
        return shortNames[parseInt(m) - 1] || '';
    };

    const getQuarterTitle = () => {
        if (!quarterPeriod) return '';
        const match = quarterPeriod.match(/^(\d{4})-T(\d)$/);
        if (!match) return quarterPeriod;
        const year = match[1];
        const q = parseInt(match[2]);
        return `${QUARTER_LABELS[q]} ${year}`;
    };

    // ======= RENDU TRIMESTRIEL =======
    const renderQuarterlyRanking = () => {
        if (!quarterlyData) return null;

        const { rankings: qRankings, months, totalDrivers, eligibleDrivers, criteria } = quarterlyData;

        return (
            <>
                {/* Info trimestre */}
                <Card className="quarterly-info-card" style={{ borderRadius: 16, marginBottom: 24, border: '2px solid #D4A853' }}>
                    <Row gutter={[16, 16]}>
                        {/* Stats visibles uniquement pour les connectes */}
                        {isLoggedIn && totalDrivers !== undefined && (
                            <>
                                <Col xs={24} sm={8}>
                                    <div style={{ textAlign: 'center' }}>
                                        <Text type="secondary">Chauffeurs analyses</Text>
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#2D3436' }}>{totalDrivers}</div>
                                    </div>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <div style={{ textAlign: 'center' }}>
                                        <Text type="secondary">Eligibles</Text>
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
                                            <CheckCircleOutlined style={{ marginRight: 8 }} />
                                            {eligibleDrivers}
                                        </div>
                                    </div>
                                </Col>
                            </>
                        )}
                        <Col xs={24} sm={isLoggedIn ? 8 : 24}>
                            <div style={{ textAlign: 'center' }}>
                                <Text type="secondary">Mois inclus</Text>
                                <div style={{ marginTop: 8 }}>
                                    {months && months.map(m => (
                                        <Tag key={m} color="blue" style={{ marginBottom: 4 }}>{formatMonthShort(m)}</Tag>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Criteres d'eligibilite */}
                    {criteria && (
                        <div style={{ marginTop: 20, padding: '16px 0 0', borderTop: '1px solid #f0f0f0' }}>
                            <Text strong style={{ display: 'block', marginBottom: 12, textAlign: 'center' }}>
                                Criteres d'eligibilite (par mois)
                            </Text>
                            <Row gutter={[12, 8]} justify="center">
                                <Col>
                                    <Tag color="orange" style={{ fontSize: 13, padding: '4px 12px' }}>
                                        {'>='} {isLoggedIn ? criteria.minOrders : '20x'} courses/mois
                                    </Tag>
                                </Col>
                                <Col>
                                    <Tag color="green" style={{ fontSize: 13, padding: '4px 12px' }}>
                                        {'>='} {isLoggedIn ? new Intl.NumberFormat('fr-FR').format(criteria.minRevenue) : '500 xxx'} FCFA/mois
                                    </Tag>
                                </Col>
                                <Col>
                                    <Tag color="blue" style={{ fontSize: 13, padding: '4px 12px' }}>
                                        {'>='} {isLoggedIn ? criteria.minHours : '13x'}h/mois
                                    </Tag>
                                </Col>
                            </Row>
                        </div>
                    )}
                </Card>

                {/* Mon analyse trimestrielle (connecte uniquement) */}
                {isLoggedIn && myQuarterlyRanking && (
                    <Card
                        style={{ borderRadius: 16, marginBottom: 24, border: myQuarterlyRanking.isEligible ? '2px solid #52c41a' : '2px solid #ff4d4f' }}
                        title={
                            <span>
                                <CrownOutlined style={{ marginRight: 8, color: '#D4A853' }} />
                                Mon analyse trimestrielle
                                {myQuarterlyRanking.isEligible ? (
                                    <Tag color="success" style={{ marginLeft: 12 }}>Eligible</Tag>
                                ) : (
                                    <Tag color="error" style={{ marginLeft: 12 }}>Non eligible</Tag>
                                )}
                            </span>
                        }
                    >
                        {/* Resume global */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                            <Col xs={12} sm={6}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Position</Text>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: '#D4A853' }}>
                                        #{myQuarterlyRanking.rank || '-'}
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12} sm={6}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Revenus total</Text>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#2D3436' }}>
                                        {formatFullCurrency(myQuarterlyRanking.totalRevenue || 0)}
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12} sm={6}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Courses total</Text>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: '#2D3436' }}>
                                        {myQuarterlyRanking.totalOrders || 0}
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12} sm={6}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 12 }}>Heures total</Text>
                                    <div style={{ fontSize: 20, fontWeight: 700, color: '#2D3436' }}>
                                        {myQuarterlyRanking.totalSupplyHours || 0}h
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* Detail par mois */}
                        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                            <Text strong style={{ display: 'block', marginBottom: 12 }}>
                                <CalendarOutlined style={{ marginRight: 8 }} />
                                Detail par mois
                            </Text>
                            <Row gutter={[12, 12]}>
                                {myQuarterlyRanking.monthlyBreakdown && myQuarterlyRanking.monthlyBreakdown.map((mb, idx) => {
                                    const elig = myQuarterlyRanking.monthlyEligibility?.[idx];
                                    const allMet = elig && elig.meetsOrders && elig.meetsRevenue && elig.meetsHours;
                                    return (
                                        <Col xs={24} sm={8} key={idx}>
                                            <div style={{
                                                background: allMet ? 'rgba(82, 196, 26, 0.05)' : 'rgba(255, 77, 79, 0.05)',
                                                borderRadius: 10,
                                                padding: '14px',
                                                border: `1px solid ${allMet ? '#b7eb8f' : '#ffa39e'}`
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                    <Tag color="blue" style={{ margin: 0 }}>{formatMonthShort(mb.month)}</Tag>
                                                    {allMet
                                                        ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                                                        : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                                                    }
                                                </div>
                                                <div style={{ fontSize: 13, marginBottom: 4 }}>
                                                    <span style={{ color: '#666' }}>Revenus: </span>
                                                    <Text strong>{formatFullCurrency(mb.revenue || 0)}</Text>
                                                    {elig && (
                                                        elig.meetsRevenue
                                                            ? <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 6 }} />
                                                            : <CloseCircleOutlined style={{ color: '#ff4d4f', marginLeft: 6 }} />
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 13, marginBottom: 4 }}>
                                                    <span style={{ color: '#666' }}>Courses: </span>
                                                    <Text strong>{mb.orders || 0}</Text>
                                                    {elig && (
                                                        elig.meetsOrders
                                                            ? <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 6 }} />
                                                            : <CloseCircleOutlined style={{ color: '#ff4d4f', marginLeft: 6 }} />
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 13 }}>
                                                    <span style={{ color: '#666' }}>Heures: </span>
                                                    <Text strong>{mb.supplyHours || 0}h</Text>
                                                    {elig && (
                                                        elig.meetsHours
                                                            ? <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 6 }} />
                                                            : <CloseCircleOutlined style={{ color: '#ff4d4f', marginLeft: 6 }} />
                                                    )}
                                                </div>
                                            </div>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    </Card>
                )}

                {/* Top 5 */}
                <Card
                    className="top15-card"
                    title={
                        <span>
                            <TrophyOutlined style={{ marginRight: 8, color: '#D4A853' }} />
                            Top 5 - {getQuarterTitle()}
                            {displayedCityName && (
                                <Tag color="blue" style={{ marginLeft: 12 }}>
                                    <EnvironmentOutlined /> {displayedCityName}
                                </Tag>
                            )}
                        </span>
                    }
                >
                    {qRankings.length === 0 ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Aucun chauffeur eligible pour ce trimestre"
                        />
                    ) : (
                        <ul className="ranking-list">
                            {qRankings.map((driver, index) => (
                                <li
                                    key={index}
                                    className={`ranking-item ${index < 3 ? 'top-3' : ''} ${driver.isMe ? 'is-me' : ''}`}
                                >
                                    <div className={`rank-badge ${getRankBadgeClass(driver.rank)}`}>
                                        {driver.rank}
                                    </div>
                                    <div className="driver-info">
                                        <div className="driver-name">
                                            {driver.firstName} {driver.lastName}
                                        </div>
                                        <div className="driver-phone">{driver.phone}</div>
                                    </div>
                                    <div className="driver-stats">
                                        <div className="driver-revenue">
                                            {formatCurrency(driver.totalRevenue)}
                                        </div>
                                        <div className="driver-orders">
                                            {driver.totalOrders} courses - {driver.totalSupplyHours}h
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </>
        );
    };

    // ======= RENDU MENSUEL =======
    const renderMonthlyRanking = () => {
        return (
            <Card
                className="top15-card"
                title={
                    <span>
                        <TrophyOutlined style={{ marginRight: 8, color: '#D4A853' }} />
                        Top 15 - {formatPeriod(period)}
                        {displayedCityName && (
                            <Tag color="blue" style={{ marginLeft: 12 }}>
                                <EnvironmentOutlined /> {displayedCityName}
                            </Tag>
                        )}
                    </span>
                }
            >
                {loading ? (
                    <div className="loading-container">
                        <Spin size="large" />
                        <Paragraph style={{ marginTop: 16, color: '#999' }}>
                            Chargement du classement...
                        </Paragraph>
                    </div>
                ) : rankings.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            displayedCityName
                                ? `Aucun classement disponible pour ${displayedCityName} cette periode`
                                : "Aucun classement disponible pour cette periode"
                        }
                    />
                ) : (
                    <ul className="ranking-list">
                        {rankings.map((driver, index) => (
                            <li
                                key={index}
                                className={`ranking-item ${index < 3 ? 'top-3' : ''} ${driver.isMe ? 'is-me' : ''}`}
                            >
                                <div className={`rank-badge ${getRankBadgeClass(driver.rank)}`}>
                                    {driver.rank}
                                </div>
                                <div className="driver-info">
                                    <div className="driver-name">
                                        {driver.firstName} {driver.lastName}
                                    </div>
                                    <div className="driver-phone">{driver.phone}</div>
                                </div>
                                <div className="driver-stats">
                                    <div className="driver-revenue">
                                        {formatCurrency(driver.totalRevenue)}
                                    </div>
                                    <div className="driver-orders">
                                        {driver.totalOrders} courses
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        );
    };

    return (
        <div className="driver-ranking-page">
            {/* Hero Section */}
            <div className="ranking-hero">
                <Title level={1}>
                    <TrophyOutlined style={{ marginRight: 12 }} />
                    Classement des Chauffeurs
                </Title>
                <Paragraph className="subtitle">
                    {isQuarterly
                        ? 'Top 5 trimestriel - Super Lots'
                        : 'Top 15 mensuel - Revenus des courses'
                    }
                </Paragraph>
            </div>

            <div className="ranking-container">
                {/* Login Prompt or User Bar */}
                {!isLoggedIn ? (
                    <div className="login-prompt">
                        <Paragraph>
                            Connectez-vous pour voir le classement de votre ville
                        </Paragraph>
                        <Link to="/chauffeurs/connexion">
                            <Button type="primary" icon={<LoginOutlined />}>
                                Se connecter
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="user-bar">
                        <div className="user-bar-info">
                            <CrownOutlined className="user-bar-icon" />
                            <div className="user-bar-details">
                                <span className="user-bar-name">
                                    {driverUser?.firstName || 'Chauffeur'} {driverUser?.lastName || ''}
                                </span>
                                <span className="user-bar-phone">
                                    {formatPhone(driverUser?.phone)}
                                </span>
                            </div>
                            {isQuarterly && myQuarterlyRanking ? (
                                <span className="user-bar-rank">
                                    #{myQuarterlyRanking.rank || '-'} - {formatCurrency(myQuarterlyRanking.totalRevenue || 0)}
                                </span>
                            ) : myRanking?.myRanking ? (
                                <span className="user-bar-rank">
                                    #{myRanking.myRanking.rank} - {formatCurrency(myRanking.myRanking.totalRevenue)}
                                </span>
                            ) : null}
                        </div>
                        <Button
                            type="link"
                            onClick={handleLogout}
                            className="user-bar-logout"
                        >
                            Deconnexion
                        </Button>
                    </div>
                )}

                {/* Filters: Period & City Selector */}
                <div className="ranking-filters">
                    <div className="filter-group">
                        <Select
                            value={period}
                            onChange={handlePeriodChange}
                            style={{ minWidth: 200 }}
                            size="large"
                            placeholder="Selectionner une periode"
                            labelRender={(props) => formatPeriod(props.value)}
                        >
                            {availablePeriods.length === 0 && period && (
                                <Select.Option key={period} value={period}>
                                    {formatPeriod(period)}
                                </Select.Option>
                            )}
                            {availablePeriods.map(p => (
                                <Select.Option key={p} value={p}>
                                    {formatPeriod(p)}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    {!isLoggedIn && (
                        <div className="filter-group">
                            <Select
                                value={selectedCity}
                                onChange={handleCityChange}
                                style={{ minWidth: 180 }}
                                size="large"
                                placeholder="Toutes les villes"
                                allowClear
                                suffixIcon={<EnvironmentOutlined />}
                            >
                                {cities.map(city => (
                                    <Select.Option key={city._id} value={city._id}>
                                        {city.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </div>

                {/* Contenu principal */}
                {loading ? (
                    <Card className="top15-card" style={{ borderRadius: 16 }}>
                        <div className="loading-container">
                            <Spin size="large" />
                            <Paragraph style={{ marginTop: 16, color: '#999' }}>
                                Chargement du classement...
                            </Paragraph>
                        </div>
                    </Card>
                ) : isQuarterly ? (
                    renderQuarterlyRanking()
                ) : (
                    renderMonthlyRanking()
                )}

                {/* Rewards Info */}
                <div className="rewards-info">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={12}>
                            <Card className="rewards-card">
                                <GiftOutlined className="reward-icon" />
                                <Title level={4}>Recompenses Mensuelles</Title>
                                <Paragraph>
                                    Les 15 premiers du classement mensuel sont recompenses chaque mois
                                </Paragraph>
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card className="rewards-card">
                                <TrophyOutlined className="reward-icon" />
                                <Title level={4}>Recompenses Trimestrielles</Title>
                                <Paragraph>
                                    Les 5 premiers du trimestre recoivent des recompenses exceptionnelles
                                </Paragraph>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    );
};

export default DriverRankingPage;
